import { useCallback, useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from '#/app/store/store'

import { ensureModal, setModalState, setModalStoreProperty } from './modals-slice'

export interface Modal {
	id: string
	state: boolean
	store: Record<string, unknown>
	setState: (state: boolean, store?: Record<string, unknown>) => void
	setStoreProperty: (key: string, value: unknown) => void
	getStoreProperty: <T = unknown>(key: string) => T | undefined
}

export function useModal(id: string): Modal {
	const dispatch = useAppDispatch()
	const entry = useAppSelector((state) => state.modals.modals[id] ?? { state: false, store: {} })

	useEffect(() => {
		dispatch(ensureModal(id))
	}, [dispatch, id])

	const setState = useCallback(
		(state: boolean, store?: Record<string, unknown>) => {
			dispatch(setModalState({ id, state, store }))
		},
		[dispatch, id]
	)

	const setStoreProperty = useCallback(
		(key: string, value: unknown) => {
			dispatch(setModalStoreProperty({ id, key, value }))
		},
		[dispatch, id]
	)

	const getStoreProperty = useCallback(
		<T = unknown>(key: string) => entry.store[key] as T | undefined,
		[entry.store]
	)

	return useMemo(
		() => ({
			id,
			state: entry.state,
			store: entry.store,
			setState,
			setStoreProperty,
			getStoreProperty,
		}),
		[id, entry.state, entry.store, setState, setStoreProperty, getStoreProperty]
	)
}
